import { Fragment } from 'react'

import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { count, dataset, text, type Keyed } from '../shared/rows'

import '../report.css'

const GMP_ITEMS = [
  '2 - High',
  '3 - Medium',
  '4 - Low',
  'Score',
  'CARs Opened',
  'CARs Closed',
  'CARs Still Open',
] as const

const SANITATION_ITEMS = [
  'CIP Swabs Collected',
  'CIP Swabs Failed',
  'Listeria Samples Collected',
  'Listeria Samples Failed',
  'Sanitation %',
  'Visual Inspection Completed',
  'Visual Inspection Failed',
] as const

const HC_FILL_WEIGHT_ITEMS = ['10oz', '12oz', 'Gallon', 'Half Gallon', 'Quart'] as const
const TEMPE_FILL_WEIGHT_ITEMS = ['Gallon', 'Half Gallon', 'Quart'] as const
const FILL_WEIGHT_ITEMS = new Set<string>([
  ...HC_FILL_WEIGHT_ITEMS,
  ...TEMPE_FILL_WEIGHT_ITEMS,
])

/** 1 - cases on hold / cases produced, per month (owner, 2026-10-19). */
const CASES_RFT = '% Cases Right the First Time'

function formatValue(n: number, item: string): string {
  if (item === 'Score') return n.toFixed(0)
  if (item === 'Sanitation %' || item === CASES_RFT) {
    const percent = Math.abs(n) <= 1 ? n * 100 : n
    return `${percent.toFixed(2)}%`
  }
  if (FILL_WEIGHT_ITEMS.has(item)) {
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function value(row: Keyed | undefined, itemOverride?: string): string {
  // A month that made nothing has no right-first-time rate: a dash, never 0%.
  if (row === undefined && itemOverride === CASES_RFT) return '—'
  if (row === undefined) return formatValue(0, itemOverride ?? '')
  const n = count(row.value)
  return formatValue(n, itemOverride ?? text(row.item) ?? '')
}

function ytdValue(rows: Keyed[], control: string, item: string): string {
  const values = rows
    .filter((row) => text(row.control_name) === control && text(row.item) === item)
    .map((row) => count(row.value))

  if (item === 'Sanitation %') {
    // The year's pass rate from its totals -- (collected - failed) / collected
    // over every month shown -- not an average of monthly percentages, which
    // gives a three-day month the same weight as a full one (owner, 2026-10-04).
    // Same inputs as the monthly figure: CIP + visual, plus Listeria for Tempe
    // only, as Power BI's SanitationPct does.
    const plant = text(rows[0]?.plant_key) ?? ''
    const inputs = (names: readonly string[]) =>
      rows
        .filter((row) => text(row.control_name) === control)
        .filter((row) => names.includes(text(row.item) ?? ''))
        .filter((row) => plant === 'tempe' || !(text(row.item) ?? '').startsWith('Listeria'))
        .reduce((total, row) => total + count(row.value), 0)
    const collected = inputs([
      'CIP Swabs Collected',
      'Visual Inspection Completed',
      'Listeria Samples Collected',
    ])
    const failed = inputs(['CIP Swabs Failed', 'Visual Inspection Failed', 'Listeria Samples Failed'])
    if (collected === 0) return '—'
    return formatValue((collected - failed) / collected, item)
  }

  return formatValue(
    values.reduce((total, n) => total + n, 0),
    item,
  )
}

function monthHeading(row: Keyed): string {
  return text(row.month_label) || ''
}

/** Numeric-aware, so "Zone 10" follows "Zone 9" rather than "Zone 1". */
function controlGroups(rows: Keyed[]): string[] {
  return Array.from(new Set(rows.map((row) => text(row.control_name) ?? '').filter(Boolean))).sort(
    (a, b) => a.localeCompare(b, undefined, { numeric: true }),
  )
}

/** Rows computed from the rows above them, styled as a result line. */
const RESULT_ITEMS = new Set<string>(['Score'])

function monthsFor(rows: Keyed[]): Array<[string, string]> {
  return Array.from(
    new Map(
      rows
        .slice()
        .sort((a, b) => String(a.month_date).localeCompare(String(b.month_date)))
        .map((row) => [text(row.month_label) ?? '', monthHeading(row)]),
    ).entries(),
  ).filter(([key]) => key !== '')
}

/** The server sends a computed YTD column labelled this; it is not a month. */
const YTD_LABEL = 'YTD'

/**
 * `showYtd` computes a YTD client-side (sanitation only). GMP, fill weights and
 * cases on hold carry their YTD as rows from the server, where it is tested.
 * `hideZeroRows` reproduces Power BI's `Row Has GMP HC Value` visual filter: an
 * item row whose every displayed cell is zero is not drawn.
 */
function Matrix({
  rows,
  items,
  firstColumn,
  emptyLabel,
  showYtd = false,
  hideZeroRows = false,
  wideNumbers = false,
}: {
  readonly rows: Keyed[]
  readonly items: readonly string[]
  readonly firstColumn: string
  readonly emptyLabel: string
  readonly showYtd?: boolean
  readonly hideZeroRows?: boolean
  /** Cases produced run to millions: a narrower label column, wider months. */
  readonly wideNumbers?: boolean
}) {
  const months = monthsFor(rows)
  const controls = controlGroups(rows)
  const byKey = new Map(
    rows.map((row) => [
      `${text(row.control_name) ?? ''}|${text(row.item) ?? ''}|${text(row.month_label) ?? ''}`,
      row,
    ]),
  )
  const hasValue = (control: string, item: string) =>
    months.some(([key]) => {
      const row = byKey.get(`${control}|${item}|${key}`)
      return row !== undefined && count(row.value) !== 0
    })
  const itemsFor = (control: string) =>
    hideZeroRows ? items.filter((item) => hasValue(control, item)) : items

  if (rows.length === 0 || months.length === 0) {
    return <Empty label={emptyLabel} />
  }

  return (
    <div className="gmp-matrix-scroll">
      <table className={wideNumbers ? 'gmp-matrix gmp-matrix-wide' : 'gmp-matrix'}>
        <thead>
          <tr>
            <th className="gmp-zone-head">{firstColumn}</th>
            {months.map(([key, label]) => (
              <th key={key}>{label}</th>
            ))}
            {showYtd ? <th>YTD</th> : null}
          </tr>
        </thead>
        <tbody>
          {controls.map((control) => (
            <Fragment key={control}>
              {/* A group heading, not a data row: the name stays pinned on the
                  left while the rest of the row is a band with no figures. */}
              <tr className="gmp-control-row" key={`${control}-heading`}>
                <td>{control}</td>
                <td colSpan={months.length + (showYtd ? 1 : 0)} />
              </tr>
              {itemsFor(control).map((item) => (
                <tr
                  key={`${control}-${item}`}
                  className={RESULT_ITEMS.has(item) ? 'gmp-score-row' : undefined}
                >
                  <td className="gmp-item-cell">{item}</td>
                  {months.map(([key]) => (
                    <td key={key}>{value(byKey.get(`${control}|${item}|${key}`), item)}</td>
                  ))}
                  {showYtd ? <td>{ytdValue(rows, control, item)}</td> : null}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function QualityDashboard({ result }: CustomReportProps) {
  const rows = dataset(result, 'primary')
  const sanitation = dataset(result, 'sanitation')
  const fillWeight = dataset(result, 'fill_weight')
  const casesOnHold = dataset(result, 'cases_on_hold')
  const selection = dataset(result, 'selection')[0]
  const plant = text(selection?.plant) ?? ''
  const year = text(selection?.year) ?? ''
  const months = monthsFor(rows).filter(([key]) => key !== YTD_LABEL)
  const sanitationPlant = text(sanitation[0]?.plant) ?? plant
  const fillWeightItems = plant === 'HC' ? HC_FILL_WEIGHT_ITEMS : TEMPE_FILL_WEIGHT_ITEMS

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {plant} GMP summary{year === '' ? '' : ` for ${year}`} - {controlGroups(rows).length}{' '}
        GMP zones - {months.length} months
      </p>

      <Card
        title={`GMP - ${plant}`}
        subtitle="One row per audit zone, whatever the control has been called (NEW, *, V2 are merged), labelled with the zone's current description. Months by CAR open date; Score = 100 - 10 per High - 5 per Medium - 1 per Low (not floored); Still Open = all opened less those closed with status 4 - Closed. Rows that are zero all year are hidden."
      >
        <Matrix
          rows={rows}
          items={GMP_ITEMS}
          firstColumn="GMP Zone"
          emptyLabel="No GMP summary rows for this selection."
          hideZeroRows
        />
      </Card>
      <Card
        title="Food Safety - Sanitation"
        subtitle={`${sanitationPlant} sanitation passed on first time, shown below the GMP matrix.`}
      >
        <Matrix
          rows={sanitation}
          items={SANITATION_ITEMS}
          firstColumn="Category"
          emptyLabel={`No ${plant} sanitation rows for this selection.`}
          showYtd
        />
      </Card>
      <Card
        title="Quality - Fill Weight Averages"
        subtitle={`${plant} monthly bottle fill averages. YTD is the average of the months.`}
      >
        <Matrix
          rows={fillWeight}
          items={fillWeightItems}
          firstColumn="Category"
          emptyLabel={`No ${plant} fill weight rows for this selection.`}
        />
      </Card>
      <Card
        title="Quality - Cases on Hold"
        subtitle={
          plant === 'HC'
            ? 'HC holds of every type, in cases: SKUs starting 1335 divided by 12, bottles divided by 4, as in Power BI. Cases produced from the bottling production data, 1335 SKUs as bottles divided by 12. % Cases Right the First Time = 1 - cases on hold / cases produced. YTD sums the cases; the YTD percentage is the year’s holds over the year’s production.'
            : 'Tempe finished-product holds (Finished Product, Finished Product or WIP) on numeric SKUs, as in Power BI. Cases produced from the bottling production data. % Cases Right the First Time = 1 - cases on hold / cases produced. YTD sums the cases; the YTD percentage is the year’s holds over the year’s production.'
        }
      >
        <Matrix
          rows={casesOnHold}
          items={['Cases On Hold', 'Cases Produced', CASES_RFT]}
          wideNumbers
          firstColumn="Category"
          emptyLabel={`No ${plant} cases on hold for this selection.`}
        />
      </Card>
    </div>
  )
}
